import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { DeviceIcon } from '@/components/DeviceIcon';
import { DLAYOUT, detailRowHeight, detailRowFont } from '@/lib/detail';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import { Tags } from 'lucide-react';

// A device on the detail page, drawn with ALL its ports as connection points.
export const DetailDeviceNode = ({ data, selected }) => {
  const { t } = useI18n();
  const showBadges = useStore((s) => s.showPortVlanBadges);
  const { deviceType, label, ports = [], vlanColor } = data;
  const rowH = detailRowHeight(ports.length);
  const font = detailRowFont(rowH);

  return (
    <div
      data-testid="detail-device-node"
      className={`overflow-hidden rounded-[10px] bg-white transition-shadow duration-150 ${
        selected ? 'ring-2 ring-[#0B5FFF] shadow-md' : 'ring-1 ring-[#1b232c]/20 hover:ring-[#2A3138]/45'
      }`}
      style={{ width: DLAYOUT.BOX_W }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 border-b border-border bg-[#FBFBFA] px-2.5"
        style={{ height: DLAYOUT.HEADER_H }}
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center">
          <DeviceIcon type={deviceType} size={26} />
        </div>
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[#111418]">{label}</span>
        {vlanColor && <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: vlanColor }} />}
      </div>

      {/* Ports */}
      {ports.length === 0 ? (
        <div
          className="flex items-center px-2.5 text-[11px] leading-snug text-muted-foreground"
          style={{ height: rowH }}
        >
          {t('detail_no_ports_member')}
        </div>
      ) : (
        <div>
          {ports.map((p) => {
            const dot = p._color || '#1b232c';
            const tagged = p._tagged || [];
            const shown = tagged.slice(0, 3);
            const extra = tagged.length - shown.length;
            const grouped = !!p._groupName;
            return (
              <div
                key={p.id}
                className="relative flex items-center gap-1.5 border-b border-border/60 px-3 last:border-b-0"
                style={{ height: rowH }}
                title={grouped ? `${p.name} · ${t('in_group')}: ${p._groupName}` : p.name}
                data-testid={`detail-port-row-${p.id}`}
              >
                <Handle type="target" position={Position.Left} id={`${p.id}__l`} className="!h-2 !w-2 !border-[1.5px] !border-[#1b232c] !bg-white" />
                <Handle type="source" position={Position.Left} id={`${p.id}__l`} className="!h-2 !w-2 !border-[1.5px] !border-[#1b232c] !bg-white" />
                {p._fromUntagged && p._color && (
                  <span className="absolute left-0 top-1/2 h-3.5 w-[3px] -translate-y-1/2 rounded-full" style={{ background: p._color }} />
                )}
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: dot }} />
                {grouped && <Tags className="h-3 w-3 shrink-0 text-muted-foreground" title={p._groupName} />}
                <span className="min-w-0 flex-1 truncate font-mono text-[#2A3138]" style={{ fontSize: font }}>{p.name}</span>
                {showBadges && p._untaggedVlanId != null && (
                  <span
                    className="shrink-0 rounded px-1 font-mono text-[9px] font-bold leading-[14px]"
                    style={{ background: `${p._untaggedColor}22`, color: p._untaggedColor }}
                    title={`Untagged VLAN ${p._untaggedVlanId}`}
                  >
                    U{p._untaggedVlanId}
                  </span>
                )}
                {showBadges &&
                  shown.map((tg) => (
                    <span
                      key={tg.vlanId}
                      className="shrink-0 rounded px-1 font-mono text-[9px] font-semibold leading-[14px]"
                      style={{ background: `${tg.color}22`, color: tg.color }}
                      title={`Tagged VLAN ${tg.vlanId}`}
                    >
                      {tg.vlanId}
                    </span>
                  ))}
                {showBadges && extra > 0 && <span className="shrink-0 text-[9px] font-semibold text-muted-foreground">+{extra}</span>}
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: dot }} />
                <Handle type="source" position={Position.Right} id={`${p.id}__r`} className="!h-2 !w-2 !border-[1.5px] !border-[#1b232c] !bg-white" />
                <Handle type="target" position={Position.Right} id={`${p.id}__r`} className="!h-2 !w-2 !border-[1.5px] !border-[#1b232c] !bg-white" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
